import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FactoryCard from "@/components/feature/FactoryCard";
import MembershipPricingBanner from "@/components/feature/MembershipPricingBanner";
import SeoHead, { BASE_URL } from "@/components/feature/SeoHead";
import { saudiFactories } from "@/mocks/saudiFactories";

type FilterType = "all" | "verified" | "dropshipping";

const SaudiPage = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");

  const filtered = saudiFactories.filter((f) => {
    if (filter === "verified" && !f.verified) return false;
    if (filter === "dropshipping" && !f.dropshipping) return false;
    if (search && !f.name.toLowerCase().includes(search.toLowerCase()) && !f.categories.join(" ").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Saudi Arabia Verified Factories & Dropshipping | SuperMiddleEast",
      "description": "Browse 8,900+ verified factories in Saudi Arabia. Sample orders, bulk orders, and dropshipping from top Saudi manufacturers.",
      "url": `${BASE_URL}/saudi-arabia`,
      "inLanguage": "en",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
          { "@type": "ListItem", "position": 2, "name": "Saudi Arabia Factories", "item": `${BASE_URL}/saudi-arabia` },
        ],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Saudi Arabia Verified Factories",
      "description": "Top verified factories in Saudi Arabia offering sample orders, bulk orders and dropshipping.",
      "url": `${BASE_URL}/saudi-arabia`,
      "numberOfItems": saudiFactories.length,
    },
  ];

  return (
    <div className="min-h-screen bg-amber-50">
      <SeoHead
        title="Saudi Arabia Verified Factories & Dropshipping | SuperMiddleEast"
        description="Browse 8,900+ verified factories in Saudi Arabia. Sample orders, bulk orders, and dropshipping available. Container & drop shipping from top Saudi manufacturers."
        keywords="Saudi Arabia factory, Saudi manufacturer, Saudi dropshipping, KSA verified supplier, buy direct Saudi Arabia, Riyadh wholesale"
        canonical={`${BASE_URL}/saudi-arabia`}
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative h-80 md:h-[420px] flex items-end overflow-hidden">
        <img src="https://public.readdy.ai/ai/img_res/e6a8061bc35e9e783089fc088054180d.jpg" alt="Saudi Arabia" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-amber-950/90 via-amber-900/50 to-amber-800/20"></div>
        <div className="relative z-10 max-w-screen-xl mx-auto px-4 md:px-8 pb-10 w-full">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-4xl">🇸🇦</span>
            <h1 className="text-3xl md:text-5xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Saudi Arabia Verified Factories &amp; Dropshipping
            </h1>
          </div>
          <p className="text-amber-200 text-base mb-4 max-w-2xl">
            Browse Saudi Arabia's top verified factories. Buy samples, place bulk orders, and dropship directly to your customers worldwide.
          </p>
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="bg-amber-500/30 border border-amber-400/50 text-amber-200 px-3 py-1 rounded-full backdrop-blur-sm flex items-center gap-1">
              <i className="ri-shield-check-fill text-amber-300"></i> 8,900+ Verified Suppliers
            </span>
            <span className="bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 px-3 py-1 rounded-full backdrop-blur-sm flex items-center gap-1">
              <i className="ri-truck-fill text-emerald-300"></i> 58% Dropshipping Ready
            </span>
            <span className="bg-white/10 border border-white/30 text-white px-3 py-1 rounded-full backdrop-blur-sm">145 Product Categories</span>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-b border-amber-100 py-8">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { v: "8,900+", l: "Verified Factories", icon: "ri-building-4-line" },
            { v: "58%", l: "Dropshipping Ready", icon: "ri-truck-line" },
            { v: "145", l: "Product Categories", icon: "ri-price-tag-3-line" },
            { v: "Vision 2030", l: "Growth Program", icon: "ri-rocket-line" },
          ].map((s) => (
            <div key={s.l} className="flex flex-col items-center">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-full mb-2">
                <i className={`${s.icon} text-amber-500 text-lg`}></i>
              </div>
              <div className="text-xl md:text-2xl font-bold text-amber-600" style={{ fontFamily: "'Playfair Display', serif" }}>{s.v}</div>
              <div className="text-gray-500 text-xs mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Dropshipping Banner */}
      <section className="bg-gradient-to-r from-emerald-700 to-emerald-900 py-8">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-emerald-500/30 rounded-xl flex-shrink-0">
              <i className="ri-truck-fill text-2xl text-emerald-300"></i>
            </div>
            <div>
              <h2 className="text-white font-bold text-lg mb-1">Saudi Arabia Dropshipping Hub</h2>
              <p className="text-emerald-200 text-sm max-w-xl">
                Factories marked with <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-bold mx-1"><i className="ri-truck-fill"></i> Dropshipping Available</span> ship directly to your customers — no inventory needed.
              </p>
            </div>
          </div>
          <button onClick={() => setFilter("dropshipping")} className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-6 py-3 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0">
            <i className="ri-truck-line"></i> View Dropship Factories
          </button>
        </div>
      </section>

      {/* Factories */}
      <section className="py-12">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5 mb-8">
            <div>
              <p className="text-amber-500 text-xs font-semibold uppercase tracking-widest mb-1">Saudi Arabia</p>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>Verified Factories &amp; Their Products</h2>
              <p className="text-gray-500 text-sm mt-1">Click any product to place a sample or bulk order directly</p>
            </div>
            <div className="relative w-full md:w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search factories or categories..." className="w-full border border-amber-200 rounded-xl pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white" />
            </div>
          </div>

          <div className="flex gap-2 mb-8 flex-wrap">
            {([
              { key: "all", label: "All Factories", icon: "ri-building-4-line" },
              { key: "verified", label: "Verified Only", icon: "ri-shield-check-line" },
              { key: "dropshipping", label: "Welcome — Container & Drop Shipping Available", icon: "ri-truck-line" },
            ] as { key: FilterType; label: string; icon: string }[]).map((tab) => (
              <button key={tab.key} onClick={() => setFilter(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${filter === tab.key ? "bg-amber-500 text-white" : "bg-white border border-amber-200 text-gray-600 hover:border-amber-400 hover:text-amber-600"}`}>
                <i className={tab.icon}></i>{tab.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${filter === tab.key ? "bg-amber-400 text-white" : "bg-amber-50 text-amber-600"}`}>
                  {tab.key === "all" ? saudiFactories.length : tab.key === "verified" ? saudiFactories.filter((f) => f.verified).length : saudiFactories.filter((f) => f.dropshipping).length}
                </span>
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-amber-100">
              <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-3"><i className="ri-search-line text-2xl text-amber-400"></i></div>
              <h3 className="font-bold text-gray-900 mb-1">No factories found</h3>
              <p className="text-gray-500 text-sm">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filtered.map((factory) => <FactoryCard key={factory.id} factory={factory} />)}
            </div>
          )}

          <div className="mt-12 bg-gradient-to-r from-amber-600 to-amber-800 rounded-2xl p-8 text-center relative overflow-hidden" id="register-cta">
            <div className="absolute inset-0 opacity-10"><div className="absolute top-0 right-0 w-64 h-64 bg-amber-300 rounded-full -translate-y-1/2 translate-x-1/2"></div></div>
            <div className="relative z-10">
              <p className="text-amber-200 text-xs font-semibold uppercase tracking-widest mb-3">Are You a Saudi Factory?</p>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>List Your Factory &amp; Start Selling Globally</h3>
              <p className="text-amber-200 text-sm mb-6 max-w-xl mx-auto">Upload your products, set sample and bulk prices, enable dropshipping — and reach millions of global buyers.</p>
              <button onClick={() => navigate("/register-supplier")} className="bg-white text-amber-700 hover:bg-amber-50 font-bold px-8 py-3 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
                <i className="ri-building-4-line"></i> Register Your Factory Now
              </button>
            </div>
          </div>
        </div>
      </section>

      <MembershipPricingBanner />
      <Footer />
    </div>
  );
};

export default SaudiPage;
